import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, Map as MapIcon, List } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Antigravity } from '../components/Antigravity';
import { PropertyListView } from '../components/PropertyListView';
import { motion } from 'framer-motion';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Centro de España y zoom para ver toda la península
const SPAIN_CENTER = [40.4, -3.7];
const SPAIN_ZOOM = 6;

// Icono VERDE para VENTA
const saleIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Icono ROJO para ALQUILER
const rentIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

function InitialViewFix() {
  const map = useMap();
  useEffect(() => {
    const interacted = { v: false };
    const onInteract = () => { interacted.v = true; };
    map.on('dragstart zoomstart', onInteract);

    const apply = () => {
      if (interacted.v) return;
      map.invalidateSize();
      map.setView(SPAIN_CENTER, SPAIN_ZOOM, { animate: false });
    };

    apply();
    const t1 = setTimeout(apply, 150);
    const t2 = setTimeout(apply, 500);
    const t3 = setTimeout(apply, 1500);

    // Resize fires when mobile browser address bar shows/hides — re-lock view
    const onResize = () => { if (!interacted.v) { map.invalidateSize(); map.setView(SPAIN_CENTER, SPAIN_ZOOM, { animate: false }); } };
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      window.removeEventListener('resize', onResize);
      map.off('dragstart zoomstart', onInteract);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Componente para resetear la vista a España
function ResetViewControl({ resetView }) {
  const map = useMap();

  useEffect(() => {
    if (resetView) {
      map.setView(SPAIN_CENTER, SPAIN_ZOOM);
    }
  }, [resetView, map]);

  return null;
}

// Componente que ajusta el mapa a los límites del polígono dibujado
function FitPolygonBounds({ coords }) {
  const map = useMap();

  useEffect(() => {
    if (coords && coords.length > 0) {
      // coords format: [[lng, lat], ...]
      const latlngs = coords.map(([lng, lat]) => [lat, lng]);
      try {
        const bounds = L.latLngBounds(latlngs);
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14, animate: true });
        }
      } catch (err) {
        // fit failed silently
      }
    }
  }, [coords, map]);

  return null;
}

// Component for polygon/rectangle/circle search
function PolygonSearch({ onPolygonCreated, onEditStart }) {
  const handleCreated = (e) => {
    const layer = e.layer;
    let coordinates;
    
    // Si es un círculo, convertirlo a polígono aproximado
    if (e.layerType === 'circle') {
      const center = layer.getLatLng();
      const radius = layer.getRadius(); // en metros
      
      // Crear un polígono con 32 puntos alrededor del círculo
      const points = 32;
      coordinates = [];
      for (let i = 0; i < points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        const lat = center.lat + (radius / 111320) * Math.cos(angle); // 111320 metros por grado de latitud
        const lng = center.lng + (radius / (111320 * Math.cos(center.lat * Math.PI / 180))) * Math.sin(angle);
        coordinates.push([lng, lat]);
      }
      coordinates.push(coordinates[0]); // Cerrar el polígono
    } 
    // Si es un rectángulo, obtener las 4 esquinas
    else if (e.layerType === 'rectangle') {
      const bounds = layer.getBounds();
      coordinates = [
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getSouth()],
        [bounds.getEast(), bounds.getNorth()],
        [bounds.getWest(), bounds.getNorth()],
        [bounds.getWest(), bounds.getSouth()] // Cerrar el rectángulo
      ];
    }
    // Si es un polígono regular
    else {
      coordinates = layer.getLatLngs()[0].map(latlng => [latlng.lng, latlng.lat]);
    }
    
    onPolygonCreated(coordinates);
  };

  return (
    <FeatureGroup>
      <EditControl
        position="topright"
        onCreated={handleCreated}
        onDrawStart={onEditStart}
        draw={{
          rectangle: {
            shapeOptions: {
              color: '#fbbf24',
              fillColor: '#fbbf24',
              fillOpacity: 0.3,
              weight: 3
            }
          },
          circle: {
            shapeOptions: {
              color: '#10b981',
              fillColor: '#10b981',
              fillOpacity: 0.3,
              weight: 3
            }
          },
          circlemarker: false,
          marker: false,
          polyline: false,
          polygon: {
            allowIntersection: false,
            drawError: {
              color: '#e74c3c',
              message: '<strong>Error:</strong> Las líneas no pueden cruzarse'
            },
            shapeOptions: {
              color: '#8b5cf6',
              fillColor: '#8b5cf6',
              fillOpacity: 0.3,
              weight: 3
            }
          }
        }}
        edit={false}
      />
    </FeatureGroup>
  );
}


// Helper para obtener la URL de la imagen principal
const getMainImageUrl = (images) => {
  if (!images || images.length === 0) return null;
  
  // Si es array de objetos
  if (typeof images[0] === 'object') {
    const portada = images.find(img => img.category === 'portada');
    const url = portada?.url || images[0]?.url;
    if (url) {
      return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
    }
  }
  // Si es array de strings
  if (typeof images[0] === 'string') {
    const url = images[0];
    return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
  }
  return null;
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { dataVersion, user } = useAuth();
  const { themes, theme: currentTheme } = useTheme();
  const [properties, setProperties] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [resetView, setResetView] = useState(0);
  const [polygonCoords, setPolygonCoords] = useState(null);
  const [drawingMode, setDrawingMode] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [viewMode, setViewMode] = useState('map'); // 'map' o 'list'
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const markerRefs = useRef({});
  const [sortOrder, setSortOrder] = useState('newest');
  const [filters, setFilters] = useState({
    listing_type: '',
    property_type: '',
    min_price: '',
    max_price: '',
    bedrooms: '',
    bathrooms: '',
    min_area: '',
    max_area: '',
    province: '',
    postal_code: ''
  });
  
  // Lista de las 52 provincias españolas
  const SPANISH_PROVINCES = [
    'A Coruña', 'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila', 
    'Badajoz', 'Barcelona', 'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón',
    'Ceuta', 'Ciudad Real', 'Córdoba', 'Cuenca', 'Girona', 'Granada', 'Guadalajara',
    'Gipuzkoa', 'Huelva', 'Huesca', 'Islas Baleares', 'Jaén', 'León', 'Lleida', 
    'Lugo', 'Madrid', 'Málaga', 'Melilla', 'Murcia', 'Navarra', 'Ourense', 'Palencia',
    'Las Palmas', 'Pontevedra', 'La Rioja', 'Salamanca', 'Santa Cruz de Tenerife',
    'Segovia', 'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo', 'Valencia',
    'Valladolid', 'Bizkaia', 'Zamora', 'Zaragoza'
  ].sort();

  // Load user favorites
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user) return;
      try {
        const res = await axios.get(`${API}/favorites`);
        setFavorites(res.data.map(p => p.property_id));
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    };
    loadFavorites();
  }, [user]);

  const toggleFavorite = async (propertyId) => {
    try {
      const isFavorite = favorites.includes(propertyId);
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${propertyId}`);
        setFavorites(favorites.filter(id => id !== propertyId));
        toast.success('Eliminado de favoritos');
      } else {
        await axios.post(`${API}/favorites/${propertyId}`, {});
        setFavorites([...favorites, propertyId]);
        toast.success('Añadido a favoritos ❤️');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      console.error('Favorites error:', status, detail, error);
      if (status === 401) {
        toast.error('Debes iniciar sesión para guardar favoritos');
      } else if (status === 404) {
        toast.error('Función de favoritos no disponible (despliega el backend)');
      } else {
        toast.error(detail || 'Error al actualizar favoritos');
      }
    }
  };


  useEffect(() => {
    loadProperties();
  }, [dataVersion, polygonCoords, sortOrder]);

  // Auto-abrir popups cuando el área dibujada contiene pocas propiedades (<=3)
  useEffect(() => {
    if (!polygonCoords || viewMode !== 'map') return;
    const visible = showOnlyFavorites
      ? properties.filter(p => favorites.includes(p.property_id))
      : properties;
    if (visible.length === 0 || visible.length > 3) return;
    // Delay to let map finish fitBounds + markers render
    const t = setTimeout(() => {
      visible.forEach(p => {
        const ref = markerRefs.current[p.property_id];
        if (ref && ref.openPopup) {
          try { ref.openPopup(); } catch (_e) { /* ignore */ }
        }
      });
    }, 700);
    return () => clearTimeout(t);
  }, [properties, polygonCoords, viewMode, showOnlyFavorites, favorites]);

  const loadProperties = async () => {
    try {
      // Si hay un polígono dibujado, usar búsqueda por polígono
      if (polygonCoords && polygonCoords.length > 0) {
        const res = await axios.post(`${API}/properties/polygon-search`, 
          { coordinates: polygonCoords }
        );
        setProperties(res.data);
        return;
      }
      
      // Si no hay polígono, usar búsqueda normal con filtros
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      if (sortOrder) params.append('sort', sortOrder);
      params.append('t', new Date().getTime());
      
      const res = await axios.get(`${API}/properties?${params.toString()}`, { 
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      setProperties(res.data);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  // Resetear vista a España cuando se hace clic en el logo
  const handleLogoClick = () => {
    setResetView(prev => prev + 1);
  };

  return (
    <>

      {/* Logo E-HOME centrado */}
      <div
        onClick={handleLogoClick}
        className="fixed top-16 sm:top-4 left-1/2 -translate-x-1/2 cursor-pointer backdrop-blur-md px-5 sm:px-8 py-2 sm:py-3 rounded-full hover:scale-105 transition-all clickable"
        style={{ background: 'rgba(5,5,8,0.85)', border: '1px solid rgba(201,168,76,0.4)', zIndex: 9989 }}
        title="Clic para ver España completa"
      >
        <h1 className="text-base sm:text-2xl font-black tracking-widest uppercase whitespace-nowrap" style={{ color: '#c9a84c', fontFamily: 'var(--font-display)' }}>
          E-HOME
        </h1>
      </div>

      {/* Toggle Vista Mapa / Lista / Favoritos */}
      <div className="fixed top-32 sm:top-20 left-4 sm:left-6 flex gap-1.5 flex-wrap" style={{ zIndex: 9989 }}>
        {[
          { mode: 'map', icon: <MapIcon size={15} />, label: 'Mapa', testId: 'view-mode-map-btn' },
          { mode: 'list', icon: <List size={15} />, label: 'Listado', testId: 'view-mode-list-btn' },
        ].map(({ mode, icon, label, testId }) => (
          <button
            key={mode}
            data-testid={testId}
            onClick={() => setViewMode(mode)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: viewMode === mode ? 'rgba(201,168,76,0.55)' : 'rgba(13,13,20,0.92)',
              color: viewMode === mode ? '#000' : '#e8e6e0',
              border: `1px solid ${viewMode === mode ? 'rgba(201,168,76,0.9)' : 'rgba(255,255,255,0.18)'}`,
              backdropFilter: 'blur(10px)',
              boxShadow: viewMode === mode ? '0 0 16px rgba(201,168,76,0.4)' : 'none',
              fontWeight: viewMode === mode ? 700 : 600,
            }}
          >
            {icon}
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
        <button
          data-testid="view-favorites-btn"
          onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
          style={{
            background: showOnlyFavorites ? 'rgba(239,68,68,0.2)' : 'rgba(13,13,20,0.92)',
            color: showOnlyFavorites ? '#f87171' : '#e8e6e0',
            border: `1px solid ${showOnlyFavorites ? 'rgba(239,68,68,0.45)' : 'rgba(255,255,255,0.18)'}`,
            backdropFilter: 'blur(10px)',
          }}
          title={showOnlyFavorites ? 'Mostrar todas' : 'Solo favoritos'}
        >
          <Heart size={15} fill={showOnlyFavorites ? 'currentColor' : 'none'} />
          <span className="hidden sm:inline">{showOnlyFavorites ? 'Todos' : 'Favoritos'}</span>
        </button>
      </div>

      {/* Botón Filtros */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="fixed top-32 sm:top-20 right-4 sm:right-6 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
        style={{
          background: showFilters ? 'rgba(201,168,76,0.25)' : 'rgba(13,13,20,0.92)',
          color: '#c9a84c',
          border: '1px solid rgba(201,168,76,0.45)',
          backdropFilter: 'blur(10px)',
          fontFamily: 'var(--font-body)',
          boxShadow: showFilters ? '0 0 12px rgba(201,168,76,0.15)' : 'none',
          zIndex: 9989,
        }}
      >
        {showFilters ? '✕ Ocultar' : '⚙ Filtros'}
      </button>

      {/* Botón Dibujar Zona */}
      {viewMode === 'map' && !drawingMode && (
        <motion.button
          data-testid="draw-area-btn"
          onClick={() => setDrawingMode(true)}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="fixed top-44 sm:top-32 right-4 sm:right-6 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
          style={{
            background: 'rgba(5,5,8,0.85)',
            color: '#00d4c8',
            border: '1px solid rgba(0,212,200,0.3)',
            backdropFilter: 'blur(8px)',
            fontFamily: 'var(--font-body)',
            zIndex: 9989,
          }}
          title="Dibujar área de búsqueda"
        >
          ✏ Zona
        </motion.button>
      )}

      {/* Mensaje flotante mientras dibuja */}
      {viewMode === 'map' && drawingMode && (
        <div
          data-testid="draw-help-box"
          className="fixed bottom-4 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-auto sm:max-w-sm px-4 py-3 rounded-xl"
          style={{ background: 'rgba(5,5,8,0.95)', border: '1px solid rgba(0,212,200,0.3)', backdropFilter: 'blur(12px)', zIndex: 9989 }}
        >
          <p className="text-xs mb-2" style={{ color: '#7a7870', fontFamily: 'var(--font-body)' }}>Rectángulo · Círculo · Polígono</p>
          <button
            data-testid="cancel-draw-btn"
            onClick={() => {
              setDrawingMode(false);
              setPolygonCoords(null);
              setResetView(prev => prev + 1);
            }}
            className="w-full py-1.5 rounded-lg text-xs font-semibold transition-opacity hover:opacity-80"
            style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
          >
            ✕ Cancelar Dibujo
          </button>
        </div>
      )}

      {/* Panel Filtros */}
      {showFilters && (
        <div className="fixed top-44 sm:top-32 right-4 sm:right-6 w-72 rounded-xl p-5 max-h-[calc(100vh-180px)] overflow-y-auto"
          style={{ background: 'rgba(13,13,20,0.97)', border: '1px solid rgba(201,168,76,0.2)', backdropFilter: 'blur(16px)', zIndex: 9989 }}>
          <h3 className="text-xs font-bold mb-4 tracking-widest" style={{ color: '#c9a84c', fontFamily: 'var(--font-display)' }}>FILTROS</h3>

          <div className="space-y-3">
            {[
              { label: 'ORDENAR', type: 'select', value: sortOrder, onChange: (v) => setSortOrder(v), options: [
                ['newest', 'Más recientes'], ['oldest', 'Más antiguos'], ['price_asc', 'Precio ↑'], ['price_desc', 'Precio ↓']
              ]},
              { label: 'OPERACIÓN', type: 'select', value: filters.listing_type, onChange: (v) => setFilters({...filters, listing_type: v}), options: [
                ['', 'Todas'], ['venta', 'Venta'], ['alquiler', 'Alquiler']
              ]},
              { label: 'TIPO', type: 'select', value: filters.property_type, onChange: (v) => setFilters({...filters, property_type: v}), options: [
                ['', 'Todos'], ['piso','Piso'], ['apartamento','Apartamento'], ['chalet','Chalet'], ['casa','Casa'], ['atico','Ático'], ['duplex','Dúplex'], ['estudio','Estudio'], ['bungalow','Bungalow'], ['finca','Finca']
              ]},
              { label: 'HABITACIONES', type: 'select', value: filters.bedrooms, onChange: (v) => setFilters({...filters, bedrooms: v}), options: [
                ['', 'Cualquiera'], ['1','1'], ['2','2'], ['3','3'], ['4','4'], ['5','5+']
              ]},
              { label: 'PROVINCIA', type: 'select', value: filters.province, onChange: (v) => setFilters({...filters, province: v}), options: [
                ['', 'Todas'], ...SPANISH_PROVINCES.map(p => [p, p])
              ]},
            ].map(f => (
              <div key={f.label}>
                <label className="text-xs block mb-1 tracking-widest" style={{ color: '#7a7870' }}>{f.label}</label>
                <select value={f.value} onChange={e => f.onChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-xs border outline-none"
                  style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.08)', fontSize: '16px' }}>
                  {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}

            <div>
              <label className="text-xs block mb-1 tracking-widest" style={{ color: '#7a7870' }}>PRECIO (€)</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" placeholder="Mín" value={filters.min_price}
                  onChange={e => setFilters({...filters, min_price: e.target.value})}
                  className="w-full px-3 py-2 rounded-lg text-xs border outline-none"
                  style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.08)', fontSize: '16px' }} />
                <input type="number" placeholder="Máx" value={filters.max_price}
                  onChange={e => setFilters({...filters, max_price: e.target.value})}
                  className="w-full px-3 py-2 rounded-lg text-xs border outline-none"
                  style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.08)', fontSize: '16px' }} />
              </div>
            </div>

            <div>
              <label className="text-xs block mb-1 tracking-widest" style={{ color: '#7a7870' }}>CÓDIGO POSTAL</label>
              <input type="text" placeholder="Ej: 28001" value={filters.postal_code}
                onChange={e => setFilters({...filters, postal_code: e.target.value})}
                className="w-full px-3 py-2 rounded-lg text-xs border outline-none"
                style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.08)', fontSize: '16px' }} />
            </div>

            <button onClick={loadProperties}
              className="w-full py-2.5 rounded-lg text-xs font-semibold tracking-widest transition-opacity hover:opacity-80"
              style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)', fontFamily: 'var(--font-body)' }}>
              APLICAR
            </button>

            <button onClick={() => {
                setSortOrder('newest');
                setFilters({ listing_type:'', property_type:'', min_price:'', max_price:'', bedrooms:'', bathrooms:'', min_area:'', max_area:'', province:'', postal_code:'' });
                loadProperties();
              }}
              className="w-full py-2 rounded-lg text-xs font-semibold transition-opacity hover:opacity-70"
              style={{ background: 'rgba(255,255,255,0.03)', color: '#7a7870', border: '1px solid rgba(255,255,255,0.06)' }}>
              Restablecer
            </button>

            <div className="flex items-center justify-center gap-4 text-xs mt-1" style={{ color: '#7a7870' }}>
              <span>🟢 Venta</span>
              <span>🔴 Alquiler</span>
            </div>
            <p className="text-xs text-center" style={{ color: '#7a7870' }}>{properties.length} propiedades</p>
          </div>
        </div>
      )}

      {/* MAPA */}
      {viewMode === 'map' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: '100dvh', zIndex: 0 }}>
          <MapContainer
          center={SPAIN_CENTER}
          zoom={SPAIN_ZOOM}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          {/* Mapa con colores vivos */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
            url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
          />
          
          <div className="leaflet-top leaflet-left" style={{ top: '180px' }}>
            <div className="leaflet-control-zoom leaflet-bar leaflet-control">
              <a className="leaflet-control-zoom-in" href="#" title="Zoom in">+</a>
              <a className="leaflet-control-zoom-out" href="#" title="Zoom out">−</a>
            </div>
          </div>
          
          {/* Fuerza zoom correcto en móvil tras mount */}
          <InitialViewFix />

          {/* Control para resetear vista */}
          <ResetViewControl resetView={resetView} />

          {/* Auto-zoom a los límites del polígono cuando se dibuja un área */}
          <FitPolygonBounds coords={polygonCoords} />
          
          {/* Polygon Search - Solo cuando drawingMode está activo */}
          {drawingMode && (
            <PolygonSearch 
              onPolygonCreated={(coords) => {
                setPolygonCoords(coords);
                setDrawingMode(false);
                toast.success('Área seleccionada. Propiedades filtradas.');
              }} 
              onEditStart={() => {
                // Ya está dibujando
              }}
            />
          )}

          {(showOnlyFavorites ? properties.filter(p => favorites.includes(p.property_id)) : properties).map((property) => {
            const imageUrl = getMainImageUrl(property.images);
            const themeData = themes[currentTheme] || themes.electric;
            
            return (
              <Marker
                key={property.property_id}
                ref={(m) => {
                  if (m) {
                    markerRefs.current[property.property_id] = m;
                  }
                }}
                position={[property.coordinates.lat, property.coordinates.lng]}
                icon={property.listing_type === 'alquiler' ? rentIcon : saleIcon}
              >
                <Popup className="custom-popup">
                  <div className={`bg-gradient-to-br ${themeData.gradient} p-4 min-w-[220px] rounded-2xl shadow-2xl border-2 border-white/20 relative`}>
                    {/* Imagen de la propiedad */}
                    {imageUrl && (
                      <img 
                        src={imageUrl} 
                        alt={property.title}
                        className="w-full h-28 object-cover rounded-xl mb-3 shadow-lg hover:scale-105 transition-transform"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <h3 className="font-black text-sm mb-2 text-white drop-shadow-lg">{property.title}</h3>
                    <p className="text-xs text-white/90 mb-2 font-medium drop-shadow">📍 {property.location}</p>
                    {/* Precio + Botón favorito (corazón) — MISMA FILA */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`text-lg font-black px-3 py-1.5 rounded-lg inline-block ${
                        property.listing_type === 'alquiler' 
                          ? 'bg-red-500 text-white' 
                          : 'bg-green-500 text-white'
                      } shadow-lg`}>
                        €{property.price.toLocaleString('es-ES')}
                        {property.listing_type === 'alquiler' && '/mes'}
                      </div>
                      <button
                        data-testid={`fav-btn-popup-${property.property_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(property.property_id);
                        }}
                        className={`p-2 rounded-lg shadow-lg transition-all hover:scale-110 border-2 ${
                          favorites.includes(property.property_id)
                            ? 'bg-red-500 border-red-300'
                            : 'bg-white border-white'
                        }`}
                        title={favorites.includes(property.property_id) ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                      >
                        <Heart 
                          size={20} 
                          fill={favorites.includes(property.property_id) ? '#ffffff' : '#ef4444'}
                          stroke={favorites.includes(property.property_id) ? '#ffffff' : '#ef4444'}
                          strokeWidth={2.5}
                        />
                      </button>
                    </div>
                    <button
                      onClick={() => navigate(`/property/${property.property_id}`)}
                      className="mt-1 bg-white text-gray-900 px-4 py-2 text-sm font-black w-full rounded-xl hover:bg-gray-100 hover:scale-105 transition-all shadow-lg"
                    >
                      Ver detalles ✨
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
        </div>
      )}

      {/* Vista Listado */}
      {viewMode === 'list' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1, overflowY: 'auto', background: '#050508', paddingTop: '10rem', paddingBottom: '5rem' }}>
          <PropertyListView
            properties={showOnlyFavorites ? properties.filter(p => favorites.includes(p.property_id)) : properties}
            favorites={favorites}
            toggleFavorite={toggleFavorite}
            showOnlyFavorites={showOnlyFavorites}
          />
        </div>
      )}
    </>
  );
};
